import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  FileText, Upload, Search, Filter, Tag as TagIcon, FilePlus,
  Folder, FolderOpen, RefreshCw, X, Eye, History as HistoryIcon,
  Trash2, ChevronDown, FileType, Calendar, Hash, User as UserIcon,
  SlidersHorizontal, Archive,
} from 'lucide-react';
import DocPreviewDrawer from '../components/document-hub/DocPreviewDrawer';
import DocUploadDialog from '../components/document-hub/DocUploadDialog';
import DocVersionsDrawer from '../components/document-hub/DocVersionsDrawer';


/**
 * Document Hub (Wave F1) — unified entity for every document type.
 *
 * Layout:
 *   ┌── Sidebar ─────────────┬── Main pane ────────────────────────┐
 *   │ • Kinds (with counts)  │ Toolbar: search + filters + upload  │
 *   │ • States               │                                     │
 *   │ • Tags catalog         │ Document grid / list                │
 *   └────────────────────────┴─────────────────────────────────────┘
 *
 * No write actions inline — opens drawers/modals for everything.
 */


const KIND_LABEL_AR = {
  supplier_invoice: 'فاتورَة مورِد',
  customer_invoice: 'فاتورَة عَميل',
  receipt_voucher:  'سَنَد قَبض',
  payment_voucher:  'سَنَد صَرف',
  bank_statement:   'كَشف بَنكي',
  contract:         'عَقد',
  purchase_order:   'أَمر شِراء',
  delivery_note:    'سَنَد تَسليم',
  credit_note:      'إشعار دائِن',
  debit_note:       'إشعار مَدين',
  expense_receipt:  'فاتورَة مَصروف',
  attachment:       'مُرفَق',
  other:            'أُخرى',
};

const STATE_LABEL_AR = {
  received:   'مُستَلَم',
  classified: 'مُصَنَّف',
  extracted:  'مُستَخرَج',
  reviewed:   'مُراجَع',
  approved:   'مُعتَمَد',
  posted:     'مُرَحَّل',
  paid:       'مَدفوع',
  archived:   'مُؤَرشَف',
  rejected:   'مَرفوض',
};

const STATE_TONE = {
  received:   'bg-slate-100 text-slate-700 border-slate-200',
  classified: 'bg-sky-100 text-sky-800 border-sky-200',
  extracted:  'bg-indigo-100 text-indigo-800 border-indigo-200',
  reviewed:   'bg-amber-100 text-amber-800 border-amber-200',
  approved:   'bg-emerald-100 text-emerald-800 border-emerald-200',
  posted:     'bg-teal-100 text-teal-800 border-teal-200',
  paid:       'bg-emerald-200 text-emerald-900 border-emerald-300',
  archived:   'bg-zinc-100 text-zinc-700 border-zinc-200',
  rejected:   'bg-red-100 text-red-800 border-red-200',
};


function formatSize(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}


function DocCard({ doc, onOpen, onVersions, onArchive }) {
  const stateInfo = STATE_TONE[doc.status] || STATE_TONE.received;
  return (
    <div
      data-testid={`doc-card-${doc.id}`}
      className="rounded-xl border bg-white p-3 hover:shadow-md transition cursor-pointer flex flex-col gap-2"
      onClick={() => onOpen?.(doc)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <div className="rounded-lg bg-slate-100 p-2 flex-shrink-0">
            <FileText className="w-4 h-4 text-slate-600" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate" title={doc.title}>
              {doc.title || doc.file_name}
            </div>
            <div className="text-[10px] text-slate-500 font-mono truncate">{doc.file_name}</div>
          </div>
        </div>
        <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${stateInfo} flex-shrink-0`}>
          {STATE_LABEL_AR[doc.status] || doc.status}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-slate-500">
        <span className="bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5 border border-indigo-200 font-semibold">
          {KIND_LABEL_AR[doc.kind] || doc.kind}
        </span>
        {doc.reference_no && (
          <span className="font-mono"><Hash className="inline w-2.5 h-2.5" />{doc.reference_no}</span>
        )}
        {doc.amount != null && (
          <span className="font-mono text-emerald-700">
            {Number(doc.amount).toFixed(2)} {doc.currency || 'SAR'}
          </span>
        )}
      </div>
      {doc.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {doc.tags.slice(0, 4).map((t) => (
            <span key={t} className="text-[9px] bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">
              {t}
            </span>
          ))}
          {doc.tags.length > 4 && (
            <span className="text-[9px] text-slate-400">+{doc.tags.length - 4}</span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between text-[10px] text-slate-400 mt-auto">
        <span>{doc.uploaded_at?.slice(0, 10)}</span>
        <span>{formatSize(doc.file_size)} · v{doc.current_version}</span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={(e) => { e.stopPropagation(); onOpen?.(doc); }}
                data-testid={`doc-preview-${doc.id}`}
                title="مُعايَنَة"
                className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold rounded bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 px-2 py-1">
          <Eye className="w-3 h-3" /> مُعايَنَة
        </button>
        <button onClick={(e) => { e.stopPropagation(); onVersions?.(doc); }}
                data-testid={`doc-versions-${doc.id}`}
                title="الإصدارات"
                className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold rounded bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 px-2 py-1">
          <HistoryIcon className="w-3 h-3" />
        </button>
        {doc.status !== 'archived' && (
          <button onClick={(e) => { e.stopPropagation(); onArchive?.(doc); }}
                  data-testid={`doc-archive-${doc.id}`}
                  title="أَرشَفَة"
                  className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold rounded bg-slate-50 hover:bg-red-100 border border-slate-200 hover:border-red-200 text-slate-700 hover:text-red-700 px-2 py-1">
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}


function Sidebar({ kinds, tags, activeKind, setActiveKind, activeStatus, setActiveStatus, activeTag, setActiveTag }) {
  return (
    <aside className="w-64 flex-shrink-0 space-y-3" data-testid="hub-sidebar">
      <div className="rounded-xl border bg-white p-2">
        <div className="text-[10px] uppercase font-semibold text-slate-500 px-2 py-1">
          الأَنواع
        </div>
        <button
          onClick={() => setActiveKind(null)}
          data-testid="kind-all"
          className={`w-full flex items-center justify-between text-xs rounded-lg px-2 py-1.5 ${
            !activeKind ? 'bg-indigo-50 text-indigo-800 font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}>
          <span>الكُل</span>
          <span className="font-mono text-[10px]">{kinds?.total ?? 0}</span>
        </button>
        {kinds?.kinds?.map((k) => (
          <button key={k} onClick={() => setActiveKind(k)}
                  data-testid={`kind-${k}`}
                  className={`w-full flex items-center justify-between text-xs rounded-lg px-2 py-1.5 ${
                    activeKind === k ? 'bg-indigo-50 text-indigo-800 font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}>
            <span>{KIND_LABEL_AR[k] || k}</span>
            <span className="font-mono text-[10px]">{kinds?.counts?.[k] ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="rounded-xl border bg-white p-2">
        <div className="text-[10px] uppercase font-semibold text-slate-500 px-2 py-1">
          الحالات
        </div>
        <button onClick={() => setActiveStatus(null)}
                data-testid="state-all"
                className={`w-full flex items-center justify-between text-xs rounded-lg px-2 py-1.5 ${
                  !activeStatus ? 'bg-indigo-50 text-indigo-800 font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}>
          <span>الكُل</span>
        </button>
        {kinds?.states?.map((s) => (
          <button key={s} onClick={() => setActiveStatus(s)}
                  data-testid={`state-${s}`}
                  className={`w-full flex items-center justify-between text-xs rounded-lg px-2 py-1.5 ${
                    activeStatus === s ? 'bg-indigo-50 text-indigo-800 font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}>
            <span>{STATE_LABEL_AR[s] || s}</span>
          </button>
        ))}
      </div>
      {tags?.items?.length > 0 && (
        <div className="rounded-xl border bg-white p-2">
          <div className="text-[10px] uppercase font-semibold text-slate-500 px-2 py-1">
            الوُسوم
          </div>
          <div className="flex flex-wrap gap-1 p-1">
            {tags.items.slice(0, 30).map((t) => (
              <button key={t.name} onClick={() => setActiveTag(activeTag === t.name ? null : t.name)}
                      data-testid={`tag-${t.name}`}
                      className={`text-[10px] font-mono rounded-full border px-2 py-0.5 ${
                        activeTag === t.name ? 'bg-indigo-600 text-white border-indigo-600'
                                              : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'}`}>
                {t.name} <span className="opacity-60">({t.count})</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}


export default function DocumentHub() {
  const [kinds, setKinds] = useState(null);
  const [tags,  setTags]  = useState(null);
  const [docs,  setDocs]  = useState([]);
  const [total, setTotal] = useState(0);
  const [skip,  setSkip]  = useState(0);
  const [limit] = useState(48);
  const [busy,  setBusy]  = useState(false);

  const [activeKind, setActiveKind]     = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const [activeTag, setActiveTag]       = useState(null);
  const [q, setQ]                       = useState('');

  const [uploadOpen, setUploadOpen]     = useState(false);
  const [previewDoc, setPreviewDoc]     = useState(null);
  const [versionsDoc, setVersionsDoc]   = useState(null);

  const loadKinds = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/document-hub/kinds');
      setKinds(data);
    } catch { /* silent */ }
  }, []);

  const loadTags = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/document-hub/tags');
      setTags(data);
    } catch { /* silent */ }
  }, []);

  const loadDocs = useCallback(async () => {
    setBusy(true);
    try {
      const params = { skip, limit };
      if (activeKind)   params.kind   = activeKind;
      if (activeStatus) params.status = activeStatus;
      if (activeTag)    params.tag    = activeTag;
      if (q)            params.q      = q;
      const { data } = await axiosInstance.get('/document-hub', { params });
      setDocs(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('فَشَل تَحميل المُستَنَدات');
    } finally { setBusy(false); }
  }, [skip, limit, activeKind, activeStatus, activeTag, q]);

  useEffect(() => { loadKinds(); loadTags(); }, [loadKinds, loadTags]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Reset skip when filters change.
  useEffect(() => { setSkip(0); /* eslint-disable-next-line */ }, [activeKind, activeStatus, activeTag, q]);

  const refreshAll = () => { loadKinds(); loadTags(); loadDocs(); toast.success('تَم التَّحديث'); };

  const archiveDoc = async (doc) => {
    if (!window.confirm(`أَرشَفَة «${doc.title || doc.file_name}»؟`)) return;
    try {
      await axiosInstance.delete(`/document-hub/${doc.id}`);
      toast.success('تَمَّت الأَرشَفَة');
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل');
    }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen className="w-6 h-6 text-indigo-600" />
            مَركَز المُستَنَدات
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            كَيان مُوَحَّد لِكُل مُستَنَدات النِّظام — فَواتير، إيصالات، عُقود، مُرفَقات.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setUploadOpen(true)}
                  data-testid="upload-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2">
            <FilePlus className="w-4 h-4" /> رَفع جَديد
          </button>
          <button onClick={refreshAll}
                  data-testid="refresh-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-2">
            <RefreshCw className="w-4 h-4" /> تَحديث
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <Sidebar
          kinds={kinds} tags={tags}
          activeKind={activeKind} setActiveKind={setActiveKind}
          activeStatus={activeStatus} setActiveStatus={setActiveStatus}
          activeTag={activeTag} setActiveTag={setActiveTag}
        />

        <div className="flex-1 space-y-3 min-w-0">
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="بَحث في العُنوان، اسم الملَف، المَرجِع…"
                     data-testid="hub-search"
                     className="w-full rounded-lg border border-slate-300 px-3 py-1.5 ps-8 text-sm" />
            </div>
            <div className="text-xs text-slate-500 font-mono" data-testid="hub-total">
              {total} مُستَنَد
            </div>
          </div>

          {busy ? (
            <div className="text-center text-slate-400 py-10 text-sm">…تَحميل</div>
          ) : docs.length === 0 ? (
            <div className="text-center text-slate-400 py-16">
              <FolderOpen className="w-12 h-12 mx-auto mb-2 opacity-40" />
              <div className="text-sm">لا تُوجَد مُستَنَدات تُطابِق الفِلتَر.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hub-grid">
              {docs.map((d) => (
                <DocCard key={d.id} doc={d}
                          onOpen={setPreviewDoc}
                          onVersions={setVersionsDoc}
                          onArchive={archiveDoc} />
              ))}
            </div>
          )}

          {total > limit && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button disabled={skip === 0}
                       onClick={() => setSkip(Math.max(0, skip - limit))}
                       data-testid="prev-page"
                       className="text-xs font-semibold rounded-lg border border-slate-300 bg-white px-3 py-1.5 disabled:opacity-40">
                السابِق
              </button>
              <span className="text-xs text-slate-500 font-mono">
                {skip + 1}-{Math.min(skip + limit, total)} / {total}
              </span>
              <button disabled={skip + limit >= total}
                       onClick={() => setSkip(skip + limit)}
                       data-testid="next-page"
                       className="text-xs font-semibold rounded-lg border border-slate-300 bg-white px-3 py-1.5 disabled:opacity-40">
                التالي
              </button>
            </div>
          )}
        </div>
      </div>

      {uploadOpen && (
        <DocUploadDialog
          onClose={() => setUploadOpen(false)}
          onUploaded={() => { setUploadOpen(false); refreshAll(); }}
        />
      )}
      {previewDoc && (
        <DocPreviewDrawer doc={previewDoc} onClose={() => setPreviewDoc(null)}
                           onMutated={refreshAll} />
      )}
      {versionsDoc && (
        <DocVersionsDrawer doc={versionsDoc}
                            onClose={() => setVersionsDoc(null)}
                            onMutated={refreshAll} />
      )}
    </div>
  );
}
